import { type PointerEvent as ReactPointerEvent, useLayoutEffect, useRef, useState } from 'react';

import { type ColumnDropPosition, dropPositionFromPointer } from './internal/columnLayout';

export type { ColumnDropPosition };

type DropTarget = {
  columnId: string;
  position: Exclude<ColumnDropPosition, 'on'>;
};

type Options = {
  container: HTMLElement | null;
  canMoveColumn: (columnId: string, targetColumnId: string, position: ColumnDropPosition) => boolean;
  moveColumn: (columnId: string, targetColumnId: string, position: ColumnDropPosition) => void;
};

const POINTER_DRAG_THRESHOLD = 4;

const headerAtPoint = (root: HTMLElement, clientX: number, clientY: number) => {
  const headers = root.querySelectorAll<HTMLElement>('[role="columnheader"][data-key]');
  for (const header of headers) {
    const rect = header.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
      continue;
    }
    if (clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom) {
      return header;
    }
  }
  return undefined;
};

export function useColumnReordering({ container, canMoveColumn, moveColumn }: Options) {
  const dropTargetRef = useRef<DropTarget | undefined>(undefined);
  const [draggedColumn, setDraggedColumn] = useState<string>();
  const [dropTarget, setDropTarget] = useState<DropTarget>();
  const containerRef = useRef(container);
  const canMoveColumnRef = useRef(canMoveColumn);
  const moveColumnRef = useRef(moveColumn);
  useLayoutEffect(() => {
    containerRef.current = container;
    canMoveColumnRef.current = canMoveColumn;
    moveColumnRef.current = moveColumn;
  }, [canMoveColumn, container, moveColumn]);

  const clearPointerDrag = () => {
    dropTargetRef.current = undefined;
    setDraggedColumn(undefined);
    setDropTarget(undefined);
  };

  const updatePointerTarget = (columnId: string, clientX: number, clientY: number) => {
    const root = containerRef.current;
    const header = root ? headerAtPoint(root, clientX, clientY) : undefined;
    const targetColumnId = header?.getAttribute('data-key') ?? undefined;
    if (!header || !targetColumnId || targetColumnId === columnId) {
      dropTargetRef.current = undefined;
      setDropTarget(undefined);
      return;
    }

    const bounds = header.getBoundingClientRect();
    const direction = getComputedStyle(header).direction === 'rtl' ? 'rtl' : 'ltr';
    const position = dropPositionFromPointer(clientX, bounds.left, bounds.width, direction);
    if (!canMoveColumnRef.current(columnId, targetColumnId, position)) {
      dropTargetRef.current = undefined;
      setDropTarget(undefined);
      return;
    }

    const next = { columnId: targetColumnId, position };
    dropTargetRef.current = next;
    setDropTarget((current) =>
      current?.columnId === next.columnId && current.position === next.position ? current : next,
    );
  };

  const onHeaderPointerDown = (event: ReactPointerEvent<HTMLElement>, columnId: string, isReorderable: boolean) => {
    if (
      !isReorderable ||
      event.button !== 0 ||
      (event.target instanceof Element && event.target.closest('button, [data-column-resizer]'))
    ) {
      return;
    }

    const pointerId = event.pointerId;
    const startX = event.clientX;
    const startY = event.clientY;
    let dragging = false;

    const end = (native: PointerEvent) => {
      if (native.pointerId !== pointerId) {
        return;
      }
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
      if (!dragging) {
        return;
      }
      native.preventDefault();
      const target = dropTargetRef.current;
      if (target) {
        moveColumnRef.current(columnId, target.columnId, target.position);
      }
      const suppressClick = (click: MouseEvent) => {
        click.preventDefault();
        click.stopPropagation();
        window.removeEventListener('click', suppressClick, true);
      };
      window.addEventListener('click', suppressClick, true);
      window.addEventListener('pointerdown', () => window.removeEventListener('click', suppressClick, true), {
        capture: true,
        once: true,
      });
      clearPointerDrag();
    };

    const move = (native: PointerEvent) => {
      if (native.pointerId !== pointerId) {
        return;
      }
      if (!dragging) {
        if (Math.hypot(native.clientX - startX, native.clientY - startY) < POINTER_DRAG_THRESHOLD) {
          return;
        }
        dragging = true;
        setDraggedColumn(columnId);
      }
      native.preventDefault();
      updatePointerTarget(columnId, native.clientX, native.clientY);
    };

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
  };

  return {
    draggedColumn,
    dropTarget,
    onHeaderPointerDown,
  };
}
